import React from 'react';
import { staticURL } from 'core/url';
import Bowser from 'bowser';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { VoxelDialog } from '@wikifactory/voxel-ui';
import { capitalize } from 'utils';
import { analytics } from 'core/analytics';

interface DownloadAppDialogProps {
  showOtherOS?: boolean;
  visible: boolean;
  onClose?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const bundle = {
  macos: 'Wikifactory-arm64.dmg',
  macosIntel: 'Wikifactory-x64.dmg',
  windows: 'Wikifactory Setup.exe',
};

const osMap: {
  [key: string]: {
    currentOSSvg: string;
    otherOS: {
      svg: string;
      bundleName: string;
    }[];
    bundleName: string;
  };
} = {
  macos: {
    currentOSSvg: 'assets/icons/macos.svg',
    otherOS: [
      {
        svg: 'assets/icons/windows.svg',
        bundleName: bundle.windows,
      },
    ],
    bundleName: bundle.macos,
  },
  windows: {
    currentOSSvg: 'assets/icons/windows.svg',
    otherOS: [
      {
        svg: 'assets/icons/macos.svg',
        bundleName: bundle.macos,
      },
    ],
    bundleName: bundle.windows,
  },
};

export const DownloadAppDialog = (props: DownloadAppDialogProps) => {
  const { showOtherOS = true, onClose, visible } = props;

  const currentOS =
    Bowser.getParser(window.navigator.userAgent)
      ?.getOS()
      ?.name?.toLowerCase() || 'windows';

  const osMatched = osMap[currentOS] || osMap.windows;

  const { currentOSSvg, otherOS, bundleName } = osMatched;

  const handleDownload = (downloadBundleName: string) => {
    const link = document.createElement('a');
    link.href = `${MAIN_URL}/download/app/${downloadBundleName}`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    analytics.sendEvent(
      'App Downloaded',
      { label: 'app-downloaded', bundle_name: downloadBundleName },
      { ga: false, facebook: false, twitter: false, amp: true },
    );
  };

  const descriptions = [
    'Now in public beta.',
    'Free for a limited time only.',
    'End to end encrypted file syncing.',
    'Works with all file formats.',
  ];

  return (
    <VoxelDialog
      onClose={onClose}
      onBackdropClick={onClose}
      disableEscapeKeyDown
      open={visible}
      actions={null}
      style={{
        // Promise level more than subheader component zIndex.
        zIndex: 1000000,
      }}
      PaperProps={{
        sx: {
          width: 482,
        },
      }}
      contentProps={{
        style: {
          maxWidth: 'unset',
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      <div className='mt-1 mx-8 flex justify-center items-center flex-col'>
        <img src={staticURL('assets/icons/folderConnected.svg')} />
        <h3 className='text-bolder w-fit mt-4'>
          Experience Wikifactory for Desktop
        </h3>
        <p className=' mx-auto text-black my-3 text-center'>
          Automatically syncs files to and from Wikifactory across your team’s
          devices.
        </p>
        <div>
          {descriptions.map((text, index, arr) => {
            const isLast = index === arr.length - 1;

            return (
              <div
                key={text}
                className={`flex text-black items-center ${
                  isLast ? '' : 'mb-2'
                }`}
              >
                <IoCheckmarkCircleOutline size={22} className=' mr-2' />
                {text}
              </div>
            );
          })}
        </div>
        <div className='mt-8 flex justify-center items-center flex-col'>
          <img
            onClick={() => {
              handleDownload(bundleName);
            }}
            className='cursor-pointer w-20 h-20'
            src={staticURL(currentOSSvg)}
          />
          <div className='flex mt-1 text-black'>
            <img
              className='text-blue cursor-pointer'
              src={staticURL('assets/icons/downloadApp.svg')}
              onClick={() => {
                handleDownload(bundleName);
              }}
            />
            <span className='ml-1'>
              Download for{' '}
              <span
                onClick={() => {
                  handleDownload(bundleName);
                }}
                className='cursor-pointer font-semibold text-black'
              >
                {currentOS === 'macos'
                  ? 'Apple Silicon'
                  : capitalize(currentOS)}
              </span>
            </span>
            {currentOS === 'macos' && (
              <span className='ml-1'>
                or{' '}
                <span
                  className='text-black font-semibold cursor-pointer '
                  onClick={() => {
                    handleDownload(bundle.macosIntel);
                  }}
                >
                  Intel
                </span>
              </span>
            )}
          </div>
          {showOtherOS && (
            <div className='flex mt-4 items-center justify-center'>
              Download for
              {otherOS.map((os, index) => (
                <div className='flex items-center' key={os.bundleName}>
                  {index !== 0 && <span className='ml-1'>or</span>}
                  <img
                    onClick={() => {
                      handleDownload(os.bundleName);
                    }}
                    className='w-8 h-8 cursor-pointer ml-1'
                    src={staticURL(os.svg)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </VoxelDialog>
  );
};
